import type { RouteRecordRaw } from 'vue-router'
import authority from '@/services/AuthService'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Root',
    component: () => import('@/layouts/ULayoutDefault.vue'),
    props: () => ({
      isLogged: authority.isActive,
    }),
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('@/views/HomeView.vue'),
      },
      {
        path: 'legal',
        name: 'LegalNotice',
        component: () => import('@/views/LegalNoticeView.vue'),
        meta: {
          withBorder: true,
        },
      },
      {
        path: 'charter',
        name: 'GoodPracticesCharter',
        component: () => import('@/views/GoodPracticesCharterView.vue'),
        meta: {
          withBorder: true,
        },
      },
      {
        path: 'contact',
        name: 'Contact',
        component: () => import('@/views/ContactView.vue'),
        meta: {
          withBorder: true,
        },
      },
      {
        path: 'about',
        name: 'About',
        component: () => import('@/views/AboutView.vue'),
        meta: {
          withBorder: true,
        },
      },
      {
        path: 'forbidden',
        name: 'Forbidden',
        component: () => import('@/components/error/ErrorForbidden.vue'),
      },
      {
        path: 'logout',
        name: 'Logout',
        component: () => import('@/views/login/Logout.vue'),
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { requiresAuth: true },
        redirect: { name: 'DashboardStories' },
        children: [
          {
            path: 'stories',
            name: 'DashboardStories',
            component: () => import('@/views/dashboardPanels/StoryEventPanel.vue'),
          },
          {
            path: 'events',
            name: 'DashboardEvents',
            component: () => import('@/views/dashboardPanels/StoryEventPanel.vue'),
          },
        ],
      },
      {
        path: 'story/:id',
        name: 'Story',
        component: () => import('@/views/StoryView.vue'),
        props: true,
      },
      {
        path: 'story/creation',
        name: 'StoryCreation',
        component: () => import('@/views/forms/StoryForm.vue'),
        meta: {
          managerOnly: true,
        },
      },
      {
        path: 'story/update/:itemId',
        name: 'StoryUpdate',
        component: () => import('@/views/forms/StoryForm.vue'),
        meta: {
          isProtectedStory: true,
        },
        props: true,
      },
      {
        path: 'event/creation',
        name: 'EventCreation',
        component: () => import('@/views/forms/EventForm.vue'),
        meta: {
          managerOnly: true,
        },
      },
      {
        path: 'event/update/:itemId',
        name: 'EventUpdate',
        component: () => import('@/views/forms/EventForm.vue'),
        meta: {
          managerOnly: true,
        },
        props: true,
      },
      {
        path: 'handshake',
        name: 'Handshake',
        component: () => import('@/views/login/HandShake.vue'),
        async beforeEnter(to) {
          const { t, log } = to.query
          await authority.logIn({ t: t as string })
          if (authority.isActive) {
            return { path: log as string }
          } else {
            return { name: 'Home' }
          }
        },
      },
      {
        path: '/collaboration-invitation-link',
        name: 'CollaborationInvitationLink',
        component: () => import('@/views/CollaborationInvitationView.vue'),
      },
      {
        path: 'search',
        name: 'Search',
        component: () => import('@/views/SearchView.vue'),
        props: (route) => ({ toSearch: route.query.q }),
      },
    ],
  },
  {
    path: '/auth',
    name: 'AuthRoot',
    component: () => import('@/layouts/ULayoutDefault.vue'),
    props: {
      isLogged: true,
    },
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: 'preferences',
        name: 'Preferences',
        component: () => import('@/views/auth/UserPreferences/UserPreferences.vue'),
      },
      {
        path: 'profile',
        name: 'AuthProfile',
        component: () => import('@/views/auth/UserProfile.vue'),
      },
      {
        path: 'groups/create',
        name: 'GroupCreate',
        component: () => import('@/views/group/GroupCreate.vue'),
        meta: {
          managerOnly: true,
        },
      },
      {
        path: 'groups/update/:groupId',
        name: 'GroupUpdate',
        component: () => import('@/views/group/GroupUpdate.vue'),
        props: true,
        meta: {
          managerOnly: true,
        },
      },
      {
        path: 'groups/:groupId',
        name: 'GroupRead',
        component: () => import('@/views/group/GroupRead.vue'),
        props: true,
      },
    ],
  },
  {
    path: '/full-screen',
    name: 'FullScreen',
    component: () => import('@/layouts/FullScreen.vue'),
    props: () => ({
      isLogged: authority.isActive,
    }),
    meta: {
      isFullScreen: true,
    },
    children: [
      {
        path: 'story/:id',
        name: 'FullScreenStory',
        component: () => import('@/views/StoryView.vue'),
        props: true,
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    component: () => import('@/layouts/ULayoutDefault.vue'),
    redirect: { name: 'NotFound' },
    children: [
      {
        path: '',
        name: 'NotFound',
        component: () => import('@/views/ErrorNotFound.vue'),
      },
    ],
  },
]

export default routes
